import type { StepperProps } from '@mui/material/Stepper';
import type { stepHeader, ChannelContextValue } from 'src/types/checkout';

import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import { Typography } from '@mui/material';
import Stepper from '@mui/material/Stepper';
import { styled } from '@mui/material/styles';
import StepLabel, { stepLabelClasses } from '@mui/material/StepLabel';
import MuiStepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

type Props = StepperProps & {
  steps: stepHeader[];
  activeStep: ChannelContextValue['activeStep'];
  channelState: ChannelContextValue['state'];
};

export function ChannelSteps({ steps, activeStep, channelState, sx, ...other }: Props) {
  let updatedActiveStep;
  if (channelState.channelType === 'EMAIL') {
    // on activeStep(1) => consider => 0 otherwise activeStep(- 1)
    updatedActiveStep = activeStep === 1 ? 0 : activeStep - 1;
  } else {
    updatedActiveStep = activeStep;
  }

  return (
    <Stepper
      orientation="vertical"
      activeStep={updatedActiveStep}
      connector={<StepConnector />}
      sx={[{ mb: { xs: 3, md: 5 } }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}
    >
      {steps.map((label) => (
        <Step key={label.header}>
          <StepLabel
            slots={{ stepIcon: StepIcon }}
            sx={{ [`& .${stepLabelClasses.label}`]: { fontWeight: 'fontWeightSemiBold' } }}
          >
            <Typography variant="h6">{label.header}</Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.disabled', display: { xs: 'none', md: 'block' } }}
            >
              {label.description}
            </Typography>
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
}

// ----------------------------------------------------------------------

const StepConnector = styled(MuiStepConnector)(({ theme }) => ({
  top: 10,
  left: 'calc(-50% + 20px)',
  right: 'calc(50% + 20px)',
  [`& .${stepConnectorClasses.line}`]: {
    borderTopWidth: 2,
    borderLeftWidth: 2,
    borderColor: theme.vars.palette.divider,
    minHeight: '50px',
  },
  [`&.${stepConnectorClasses.active}, &.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: { borderColor: theme.vars.palette.primary.main },
  },
}));

type StepIconProps = {
  active?: boolean;
  completed?: boolean;
};

function StepIcon({ active, completed }: StepIconProps) {
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        color: 'text.disabled',
        justifyContent: 'center',
        ...(active && { color: 'primary.main' }),
      }}
    >
      {completed ? (
        <Iconify icon="eva:checkmark-fill" sx={{ color: 'primary.main' }} />
      ) : (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'currentColor',
          }}
        />
      )}
    </Box>
  );
}
